import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import z from "zod";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import { EngagementSettingsSchema } from "@performyard/models";
import {
	AnimatePresence,
	ButtonStandard,
	Checkbox,
	FormField,
	FormHandle,
	FormSingleSelect,
	FormSubscribe,
	Icon,
	InputWrapper,
	LogoLoader,
	PYForm,
	PageBanner,
	ResetButton,
	SingleSelectInput,
	SubmitButton,
	SubtleTopSlideIn,
	Text,
	Tooltip,
	VALIDATE_ON,
	toast,
} from "@performyard/py-components";

import EngagementSettingsSurveySchedule from "../components/EngagementSettingsSurveySchedule";
import ManuallySelectedQuestionsList from "../components/ManuallySelectedQuestionsList";
import { SettingLabel } from "../components/SettingLabel";
import SettingsSection from "../components/SettingsSection";
import ManuallySelectQuestions from "./ManuallySelectQuestions";

import { useCompanyEngagementSettings } from "../../../../../../api/hooks/defs/company";
import { useEngagementSurveyQuestions } from "../../../../../../api/hooks/defs/engagement";
import { useSetCompanyEngagementSettings } from "../../../../../../api/hooks/defs/engagement";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";
import useUnsavedChanges from "../../../../../../hooks/useUnsavedChanges";

const EngagementQuestionsSchemaSlice = EngagementSettingsSchema.pick({
	factors_frequency: true,
	enps_frequency: true,
	ask_additional_enps_feedback_question: true,
	enps_range: true,
	custom_questions_frequency: true,
	custom_questions_include_all: true,
	additional_feedback_question_frequency: true,
	question_cycle: true,
	selection_method: true,
	manually_selected_questions: true,
	manually_selected_questions_frequency: true,
});

const questionFrequencyOptions: { value: number; label: string }[] = [
	{
		value: 1,
		label: "Every Pulse Survey",
	},
	{
		value: 2,
		label: "Every 2nd Pulse Survey",
	},
	{
		value: 3,
		label: "Every 3rd Pulse Survey",
	},
	{
		value: 4,
		label: "Every 4th Pulse Survey",
	},
];

const enpsQuestionFrequencyOptions: { value: number; label: string }[] = [
	...questionFrequencyOptions,
	{ value: 0, label: "Never" },
];

const selectionMethodOptions: { value: "automatic" | "manual"; label: string }[] = [
	{ value: "automatic", label: "Automatic Selection" },
	{ value: "manual", label: "Manual Selection" },
];

const customQuestionsFrequencyOptions: { value: number; label: string }[] = [
	...questionFrequencyOptions,
	{ value: 0, label: "Never" },
];

const additionalFeedbackQuestionFrequencyOptions: { value: number; label: string }[] = [
	...questionFrequencyOptions,
	{ value: 0, label: "Never" },
];

const zeroToTenOptions = Array.from(Array(11).keys()).map((index) => ({
	value: index,
	label: index.toString(),
}));

const enpsRangeOptions: { value: number | null; label: string }[] = [{ value: null, label: "-" }, ...zeroToTenOptions];

const FactorsSelectInput = ({
	value,
	onChange,
	enpsFrequency,
}: {
	value: number;
	onChange: (value: number) => void;
	enpsFrequency: number;
}) => {
	useEffect(() => {
		if (enpsFrequency !== 1) {
			onChange(1);
		}
	}, [enpsFrequency]);
	return (
		<SingleSelectInput<number>
			value={value}
			onChange={(value) => onChange(value ?? 1)}
			options={questionFrequencyOptions}
			dangerouslySetClassName="max-w-[197px] ml-auto"
		/>
	);
};

const EnpsSelectInput = ({
	value,
	onChange,
	factorsFrequency,
	manualQuestionsFrequency,
}: {
	value: number;
	onChange: (value: number) => void;
	factorsFrequency: number;
	manualQuestionsFrequency: number;
}) => {
	useEffect(() => {
		if (factorsFrequency !== 1 || manualQuestionsFrequency !== 1) {
			onChange(1);
		}
	}, [factorsFrequency, manualQuestionsFrequency]);
	return (
		<SingleSelectInput<number>
			value={value}
			onChange={(value) => onChange(value ?? 1)}
			options={enpsQuestionFrequencyOptions}
			dangerouslySetClassName="max-w-[197px] ml-auto"
		/>
	);
};

const ManuallySelectedQuestionsSelectInput = ({
	value,
	onChange,
	enpsFrequency,
}: {
	value: number;
	onChange: (value: number) => void;
	enpsFrequency: number;
}) => {
	useEffect(() => {
		if (enpsFrequency !== 1) {
			onChange(1);
		}
	}, [enpsFrequency]);
	return (
		<SingleSelectInput<number>
			value={value}
			onChange={(value) => onChange(value ?? 1)}
			options={questionFrequencyOptions}
			dangerouslySetClassName="max-w-[197px] ml-auto"
		/>
	);
};

const QuestionsTab = () => {
	const formRef = useRef<FormHandle<typeof EngagementQuestionsSchemaSlice>>(null);
	const [isSelectingQuestions, setIsSelectingQuestions] = useState(false);
	const [showManualQuestionsError, setShowManualQuestionsError] = useState(false);
	const { isLoading: isLoadingEngagementSurveyQuestions } = useEngagementSurveyQuestions();
	const { value: isEngagementQuestionSelectionEnabled, query: isEngagementQuestionSelectionEnabledQuery } =
		useFeatureFlagQuery("ff-temp-is-engagement-question-selection-enabled", false);

	const { data: engagementSettings, isLoading } = useCompanyEngagementSettings();

	const { mutate: setCompanyEngagementSettings, isPending: isSettingCompanyEngagementSettings } =
		useSetCompanyEngagementSettings({
			onSuccess: () => {
				toast.success("Your changes have been saved.");
				setIsSafe();
				formRef.current?.reset();
			},
		});

	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();
	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

	// Reset form after data loads to ensure it's not dirty on initial load
	useEffect(() => {
		if (engagementSettings && formRef.current) {
			formRef.current.reset();
		}
	}, [isLoading]);

	useEffect(() => {
		if (typeof document === "undefined") return;
		setPortalContainer(document.getElementById("pulse-survey-settings"));
	}, []);

	// handle the visibility of the form and the manually select questions section when in manual select mode
	useEffect(() => {
		if (!portalContainer) {
			return;
		}

		const formElement = portalContainer.querySelector<HTMLElement>("#engagement-questions-form");
		const portalElement = portalContainer.querySelector<HTMLElement>("#manually-select-questions");

		const toggleVisibility = (element: HTMLElement | null | undefined, shouldHide: boolean) => {
			if (!element) {
				return;
			}
			element.classList.toggle("hidden", shouldHide);
			if (shouldHide) {
				element.setAttribute("aria-hidden", "true");
				element.style.pointerEvents = "none";
			} else {
				element.removeAttribute("aria-hidden");
				element.style.removeProperty("pointer-events");
			}
		};

		if (!isSelectingQuestions) {
			toggleVisibility(formElement, false);
			toggleVisibility(portalElement, true);
			return;
		}

		window.scrollTo({ top: 0, behavior: "smooth" });
		toggleVisibility(formElement, true);
		toggleVisibility(portalElement, false);
	}, [isSelectingQuestions, portalContainer]);

	const handleSubmit = (values: z.infer<typeof EngagementQuestionsSchemaSlice>) => {
		// Check if any frequency changed and reset question_cycle to 1 if so
		const initialValues = engagementSettings?.engagement;
		const shouldResetQuestionCycle =
			values.enps_frequency !== initialValues?.enps_frequency ||
			values.factors_frequency !== initialValues?.factors_frequency ||
			values.custom_questions_frequency !== initialValues?.custom_questions_frequency ||
			values.additional_feedback_question_frequency !== initialValues?.additional_feedback_question_frequency ||
			values.manually_selected_questions_frequency !== initialValues?.manually_selected_questions_frequency;

		// Cross-field dependency: when enps or factors frequency changes from default,
		// reset the other to 1 as well (matching old Main tab behavior)
		const adjustedValues = { ...values };

		if (values.enps_frequency !== initialValues?.enps_frequency && values.enps_frequency !== 1) {
			adjustedValues.factors_frequency = 1;
			adjustedValues.manually_selected_questions_frequency = 1;
		}

		if (values.factors_frequency !== initialValues?.factors_frequency && values.factors_frequency !== 1) {
			adjustedValues.enps_frequency = 1;
		}

		if (
			values.manually_selected_questions_frequency !== initialValues?.manually_selected_questions_frequency &&
			values.manually_selected_questions_frequency !== 1
		) {
			adjustedValues.enps_frequency = 1;
		}

		// Special case: if eNPS frequency is set to Never (0),
		// uncheck the additional feedback checkbox
		if (values.enps_frequency === 0) {
			adjustedValues.ask_additional_enps_feedback_question = false;
		}

		// Special case: if custom questions frequency is set to Never (0),
		// restore customQuestionsIncludeAll to its initial value
		if (values.custom_questions_frequency === 0) {
			adjustedValues.custom_questions_include_all = initialValues?.custom_questions_include_all || false;
		}

		// Reset question_cycle to 1 if any frequency changed
		if (shouldResetQuestionCycle) {
			adjustedValues.question_cycle = 1;
		}

		setCompanyEngagementSettings(adjustedValues);
	};

	if (isLoading || isLoadingEngagementSurveyQuestions || isEngagementQuestionSelectionEnabledQuery.isLoading)
		return (
			<div className="flex h-[400px] w-full items-center justify-center">
				<LogoLoader textArray={[]} mainText="Loading Settings..." />
			</div>
		);

	if (!engagementSettings) throw new Error("No engagement settings found");

	// Prepare default values with proper structure
	const defaultValues = EngagementQuestionsSchemaSlice.parse({
		factors_frequency: engagementSettings?.engagement.factors_frequency || 1,
		enps_frequency: engagementSettings?.engagement.enps_frequency ?? 1,
		ask_additional_enps_feedback_question:
			engagementSettings?.engagement.ask_additional_enps_feedback_question || false,
		enps_range: {
			below: engagementSettings?.engagement.enps_range?.below ?? null,
			above: engagementSettings?.engagement.enps_range?.above ?? null,
		},
		custom_questions_frequency: engagementSettings?.engagement.custom_questions_frequency ?? 0,
		custom_questions_include_all: engagementSettings?.engagement.custom_questions_include_all || false,
		additional_feedback_question_frequency:
			engagementSettings?.engagement.additional_feedback_question_frequency ?? 0,
		question_cycle: engagementSettings?.engagement.question_cycle || 1,
		selection_method: engagementSettings?.engagement.selection_method,
		manually_selected_questions: engagementSettings?.engagement.manually_selected_questions,
		manually_selected_questions_frequency:
			engagementSettings?.engagement.manually_selected_questions_frequency || 1,
	});

	return (
		<>
			<PYForm
				ref={formRef}
				validateOn={[VALIDATE_ON.ON_SUBMIT]}
				schema={EngagementQuestionsSchemaSlice}
				defaultValues={defaultValues}
				onSubmit={({ value }) => {
					// handle manual validations outside of the schema
					if (value.selection_method === "manual" && value.manually_selected_questions.length === 0) {
						setShowManualQuestionsError(true);
						return;
					}

					handleSubmit(value);
				}}
				id="engagement-questions-form"
			>
				<div className="w-full pt-10" key="form-content">
					{isEngagementQuestionSelectionEnabled && (
						<>
							<SettingsSection
								title="Selection Method"
								description="Determine whether questions are automatically varied for diverse insights or manually curated for consistent organizational measurement."
							>
								<FormSingleSelect<"automatic" | "manual">
									id="selection_method"
									labelPosition="left"
									justify="between"
									label={
										<SettingLabel
											title="Question Selection Method"
											description="Determine how questions are selected for each pulse survey."
										/>
									}
									options={selectionMethodOptions}
									dangerouslySetClassName="max-w-[197px] ml-auto"
								/>
							</SettingsSection>

							<div className="h-8 w-full border-t border-solid border-neutral-2" />
						</>
					)}
					<SettingsSection
						title="Pulse Survey Questions"
						description="Set preferences for the types of questions to be included in the employee pulse surveys."
					>
						<div className="flex flex-col gap-8">
							<FormSubscribe
								id="selectionMethodFactorQuestionsSubscribe"
								selector={(state) => [state?.values.selection_method]}
							>
								{([selectionMethod]) => {
									const shouldShow =
										!isEngagementQuestionSelectionEnabled || selectionMethod === "automatic";
									return (
										<div className={shouldShow ? "block" : "hidden"}>
											<FormSubscribe
												id="factorsFrequencySubscribe"
												selector={(state) => ({
													enpsFrequency: state.values.enps_frequency,
												})}
											>
												{({ enpsFrequency }) => {
													return (
														<FormField<number>
															id="factors_frequency"
															labelPosition="left"
															justify="between"
															label={
																<SettingLabel
																	title="Ask Factor Questions"
																	description="Set how often the Factor questions should be included in employee pulse surveys."
																/>
															}
															input={(field) => {
																return (
																	<FactorsSelectInput
																		value={field.value}
																		onChange={field.onChange}
																		enpsFrequency={enpsFrequency}
																	/>
																);
															}}
														/>
													);
												}}
											</FormSubscribe>
										</div>
									);
								}}
							</FormSubscribe>
							<div className="flex flex-col gap-4">
								<FormSubscribe
									id="enpsFrequencySubscribe"
									selector={(state) => ({
										factorsFrequency: state.values.factors_frequency,
										manualQuestionsFrequency: state.values.manually_selected_questions_frequency,
									})}
								>
									{({ factorsFrequency, manualQuestionsFrequency }) => {
										return (
											<FormField<number>
												id="enps_frequency"
												labelPosition="left"
												justify="between"
												label={
													<SettingLabel
														title="Ask eNPS Question"
														description="Set how often the eNPS question should be included in employee pulse surveys."
													/>
												}
												input={(field) => (
													<EnpsSelectInput
														value={field.value}
														onChange={field.onChange}
														factorsFrequency={factorsFrequency}
														manualQuestionsFrequency={manualQuestionsFrequency}
													/>
												)}
											/>
										);
									}}
								</FormSubscribe>

								<FormSubscribe
									id="enpsFrequencyAdditionalFeedbackSubscribe"
									selector={(state) => ({
										enpsFrequency: state.values.enps_frequency,
										askAdditionalFeedback: state.values.ask_additional_enps_feedback_question,
									})}
								>
									{({ enpsFrequency, askAdditionalFeedback }) => {
										return (
											<>
												<FormField<boolean>
													id="ask_additional_enps_feedback_question"
													labelPosition="right"
													disabled={enpsFrequency === 0}
													label={
														<Text
															size="paragraph-3"
															weight="regular"
															color="neutral-9"
															dangerouslySetClassName={
																enpsFrequency === 0 ? "opacity-disabled" : ""
															}
														>
															Ask for Additional eNPS Feedback
														</Text>
													}
													input={(field) => (
														<div className="flex items-center gap-2">
															<Checkbox
																id={`ask_additional_enps_feedback_question-checkbox`}
																checked={enpsFrequency === 0 ? false : field.value}
																onCheckedChange={field.onChange}
																disabled={enpsFrequency === 0}
															/>
														</div>
													)}
												/>

												{askAdditionalFeedback && enpsFrequency !== 0 && (
													<div className="ml-6">
														<FormSubscribe
															id="enpsRangeSubscribe"
															selector={(state) => ({
																rangeBelow: state.values.enps_range?.below,
																rangeAbove: state.values.enps_range?.above,
															})}
														>
															{({ rangeBelow, rangeAbove }) => {
																// Filter "below" options: must be less than "above" value
																const belowOptions = enpsRangeOptions.filter(
																	(option) => {
																		if (option.value === null) return true;
																		if (rangeAbove === null) return true;
																		return (option.value as number) < rangeAbove;
																	}
																);

																// Filter "above" options: must be greater than "below" value
																const aboveOptions = enpsRangeOptions.filter(
																	(option) => {
																		if (option.value === null) return true;
																		if (rangeBelow === null) return true;
																		return (option.value as number) > rangeBelow;
																	}
																);

																return (
																	<div className="flex items-center justify-between">
																		<div className="max-w-[288px] space-y-2">
																			<Text
																				size="paragraph-3"
																				weight="semi-bold"
																				color="neutral-9"
																			>
																				Set Feedback Ranges
																			</Text>
																			<Text
																				size="paragraph-3"
																				weight="regular"
																				color="neutral-9"
																			>
																				eNPS answers in these ranges will
																				trigger an additional feedback field in
																				your pulse surveys.
																			</Text>
																		</div>
																		<div className="flex flex-col gap-1">
																			<FormSingleSelect<number | null>
																				id="enps_range.below"
																				labelPosition="right"
																				label={
																					<Text
																						size="paragraph-3"
																						weight="regular"
																						color="neutral-9"
																					>
																						And Below
																					</Text>
																				}
																				options={belowOptions}
																				dangerouslySetClassName="max-w-[60px]"
																			/>
																			<FormSingleSelect<number | null>
																				id="enps_range.above"
																				labelPosition="right"
																				label={
																					<Text
																						size="paragraph-3"
																						weight="regular"
																						color="neutral-9"
																					>
																						And Above
																					</Text>
																				}
																				options={aboveOptions}
																				dangerouslySetClassName="max-w-[60px]"
																			/>
																		</div>
																	</div>
																);
															}}
														</FormSubscribe>
													</div>
												)}
											</>
										);
									}}
								</FormSubscribe>
							</div>
							<FormSubscribe
								id="selectionMethodCustomQuestionsSubscribe"
								selector={(state) => [state?.values.selection_method]}
							>
								{([selectionMethod]) => {
									const shouldShow =
										!isEngagementQuestionSelectionEnabled || selectionMethod === "automatic";
									return (
										<div className={shouldShow ? "block" : "hidden"}>
											<div className="flex flex-col gap-4">
												<FormSingleSelect<number>
													id="custom_questions_frequency"
													labelPosition="left"
													justify="between"
													label={
														<SettingLabel
															title="Ask Custom Questions"
															description="Set how often custom questions should be included in employee pulse surveys."
														/>
													}
													options={customQuestionsFrequencyOptions}
													dangerouslySetClassName="max-w-[197px] ml-auto"
												/>

												<FormSubscribe
													id="customQuestionsFrequencySubscribe"
													selector={(state) => ({
														customQuestionsFrequency:
															state.values.custom_questions_frequency,
														includeAll: state.values.custom_questions_include_all,
													})}
												>
													{({ customQuestionsFrequency }) => {
														return (
															<FormField<boolean>
																id="custom_questions_include_all"
																labelPosition="right"
																disabled={customQuestionsFrequency === 0}
																label={
																	<div
																		className={`flex items-center ${customQuestionsFrequency === 0 ? "opacity-disabled" : ""}`}
																	>
																		<Text
																			size="paragraph-3"
																			weight="regular"
																			color="neutral-9"
																		>
																			Include all custom questions
																		</Text>
																		<div className="-mt-2 [&>button]:p-0">
																			<Tooltip
																				disabled={
																					customQuestionsFrequency === 0
																				}
																				content="When selected, pulse surveys will add any active custom questions to the end of the survey as opposed to selecting a randomized subset of custom questions."
																			>
																				<Icon
																					size={12}
																					icon={faInfoCircle}
																					dangerouslySetClassName="hover:text-neutral-7"
																				/>
																			</Tooltip>
																		</div>
																	</div>
																}
																input={(field) => (
																	<div className="flex items-center">
																		<Checkbox
																			id={`custom_questions_include_all-checkbox`}
																			checked={
																				customQuestionsFrequency === 0
																					? false
																					: field.value
																			}
																			onCheckedChange={field.onChange}
																			disabled={customQuestionsFrequency === 0}
																		/>
																	</div>
																)}
															/>
														);
													}}
												</FormSubscribe>
											</div>
										</div>
									);
								}}
							</FormSubscribe>
							<FormSingleSelect<number>
								id="additional_feedback_question_frequency"
								labelPosition="left"
								justify="between"
								label={
									<SettingLabel
										title="Ask Additional Feedback Question"
										description="Ask an open-ended text question at the end of the pulse survey to gather any additional feedback."
									/>
								}
								options={additionalFeedbackQuestionFrequencyOptions}
								dangerouslySetClassName="max-w-[197px] ml-auto"
							/>
							<FormSubscribe
								id="selectionMethodManualQuestionsFrequencySubscribe"
								selector={(state) => [state?.values.selection_method]}
							>
								{([selectionMethod]) => {
									const shouldShow =
										isEngagementQuestionSelectionEnabled && selectionMethod === "manual";
									return (
										<div className={shouldShow ? "block" : "hidden"}>
											<FormSubscribe
												id="manualQuestionsFrequencySubscribe"
												selector={(state) => ({
													enpsFrequency: state.values.enps_frequency,
												})}
											>
												{({ enpsFrequency }) => {
													return (
														<FormField<number>
															id="manually_selected_questions_frequency"
															labelPosition="left"
															justify="between"
															label={
																<SettingLabel
																	title="Ask Manually Selected Questions"
																	description="Set how often the manually selected questions should be included in employee pulse surveys."
																/>
															}
															input={(field) => {
																return (
																	<ManuallySelectedQuestionsSelectInput
																		value={field.value}
																		onChange={field.onChange}
																		enpsFrequency={enpsFrequency}
																	/>
																);
															}}
														/>
													);
												}}
											</FormSubscribe>
										</div>
									);
								}}
							</FormSubscribe>
							<FormSubscribe
								id="selectionMethodSubscribe"
								selector={(state) => [state?.values.selection_method]}
							>
								{([selectionMethod]) => {
									const shouldShow =
										isEngagementQuestionSelectionEnabled && selectionMethod === "manual";
									return (
										<div className={shouldShow ? "block" : "hidden"}>
											<FormField<Array<{ question_id: string; order: number }>>
												id="manually_selected_questions"
												labelPosition="left"
												justify="between"
												input={(field) => {
													return (
														<div className="w-[546px] space-y-4">
															<InputWrapper
																id="selection_method_select_questions"
																labelPosition="left"
																justify="between"
																label={
																	<SettingLabel
																		title="Manually Selected Questions"
																		description="Choose up to 32 enabled custom or factor questions to include in your pulse surveys."
																	/>
																}
															>
																<div className="w-fit">
																	{" "}
																	<ButtonStandard
																		type="button"
																		size="medium"
																		onClick={() => {
																			setIsUnsafe();
																			setIsSelectingQuestions(true);
																		}}
																	>
																		Manage Questions
																	</ButtonStandard>
																</div>
															</InputWrapper>
															<ManuallySelectedQuestionsList
																questions={field?.value ?? []}
																onChange={(questions) => {
																	field.onChange(questions);
																}}
																onDelete={(questionId) => {
																	field.onChange(
																		field.value.filter(
																			(q) => q.question_id !== questionId
																		)
																	);
																}}
															/>
															<AnimatePresence>
																{showManualQuestionsError && (
																	<SubtleTopSlideIn>
																		<PageBanner
																			icon={faTriangleExclamation}
																			color="red"
																			mainTitle="Select at least one question to save your changes"
																		/>
																	</SubtleTopSlideIn>
																)}
															</AnimatePresence>
															{isSelectingQuestions && portalContainer
																? createPortal(
																		<div
																			id="manually-select-questions"
																			className="absolute inset-0 z-50 h-full w-full bg-neutral-0"
																		>
																			<ManuallySelectQuestions
																				onBack={() => {
																					setIsSelectingQuestions(false);
																				}}
																				onSelectQuestions={(questionIds) => {
																					setShowManualQuestionsError(false);
																					field.onChange(
																						questionIds.map(
																							(questionId, index) => ({
																								question_id: questionId,
																								order: index,
																							})
																						)
																					);
																				}}
																				initialSelectedQuestionIds={field?.value?.map(
																					(question) => question.question_id
																				)}
																			/>
																		</div>,
																		portalContainer
																	)
																: null}
														</div>
													);
												}}
											/>
										</div>
									);
								}}
							</FormSubscribe>
							<FormSubscribe
								id="engagementSettingsSurveySchedule"
								selector={(state) => ({
									customQuestionsFrequency: state.values.custom_questions_frequency,
									customQuestionsIncludeAll: state.values.custom_questions_include_all,
									additionalFeedbackQuestionFrequency:
										state.values.additional_feedback_question_frequency,
									factorsFrequency: state.values.factors_frequency,
									enpsFrequency: state.values.enps_frequency,
									questionCycle: state.values.question_cycle,
									manuallySelectedQuestions: state.values.manually_selected_questions,
									manuallySelectedQuestionsFrequency:
										state.values.manually_selected_questions_frequency,
									selectionMethod: state.values.selection_method,
								})}
							>
								{(state) => {
									return engagementSettings?.engagement.next_survey_date ? (
										<EngagementSettingsSurveySchedule
											frequency={engagementSettings?.engagement.frequency}
											nextSurveyDate={engagementSettings?.engagement.next_survey_date?.toISOString()}
											questionCycle={state.questionCycle}
											factorsFrequency={state.factorsFrequency}
											enpsFrequency={state.enpsFrequency}
											customQuestionsFrequency={state.customQuestionsFrequency}
											customQuestionsIncludeAll={state.customQuestionsIncludeAll}
											additionalFeedbackQuestionFrequency={
												state.additionalFeedbackQuestionFrequency
											}
											sendReminderEmails={engagementSettings?.engagement.send_reminder_emails}
											manuallySelectedQuestions={state.manuallySelectedQuestions}
											manuallySelectedQuestionsFrequency={
												state.manuallySelectedQuestionsFrequency
											}
											selectionMethod={state.selectionMethod}
										/>
									) : null;
								}}
							</FormSubscribe>

							<FormSubscribe
								id="engagementQuestionsUnsavedChanges"
								selector={(state) => [state?.isDirty]}
							>
								{([isDirty]) => {
									if (isDirty) {
										setIsUnsafe();
									}
									return null;
								}}
							</FormSubscribe>
						</div>
					</SettingsSection>
				</div>
				<FormSubscribe id="engagementQuestionsUnsavedChangesFooter" selector={(state) => [state?.isDirty]}>
					{([isDirty]) => {
						return (
							<div
								data-form-dirty={isDirty}
								className="group flex w-full justify-end gap-2"
								key="form-footer"
							>
								<div className="w-fit group-data-[form-dirty=false]:hidden">
									<ResetButton
										onReset={() => {
											setShowManualQuestionsError(false);
										}}
										color="neutral"
										variant="outline"
									>
										Discard
									</ResetButton>
								</div>
								<div className="w-fit">
									<SubmitButton
										color="green"
										loading={isSettingCompanyEngagementSettings}
										disabled={isSettingCompanyEngagementSettings || !isDirty}
									>
										Save Changes
									</SubmitButton>
								</div>
							</div>
						);
					}}
				</FormSubscribe>
			</PYForm>
		</>
	);
};

export default QuestionsTab;
